"use client";

import { motion } from "framer-motion";
import { Gauge, ShieldCheck, Search, Server, SquarePlay as PlaySquare, Download, FileText, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const protocols = [{
  title: "Speed & Latency Analysis",
  description: "We conduct rigorous multi-server speed tests across global locations, measuring baseline degradation, ping spikes, and sustained throughput under heavy load.",
  icon: Gauge
}, {
  title: "Encryption Verification",
  description: "Our team intercepts and analyzes packet data using Wireshark to ensure AES-256 or ChaCha20 encryption is actively applied without fallback vulnerabilities.",
  icon: ShieldCheck
}, {
  title: "Leak Detection (DNS/WebRTC)",
  description: "We force edge-case network drops and browser vulnerabilities to test for IPv6, DNS, and WebRTC leaks, ensuring your true IP remains hidden at all times.",
  icon: Search
}, {
  title: "Server Network Auditing",
  description: "We verify the physical vs. virtual status of server locations, checking for RAM-only infrastructure and assessing the provider's hardware ownership claims.",
  icon: Server
}, {
  title: "Streaming Unblocking",
  description: "Continuous automated and manual testing against major streaming platforms to verify consistent access, bypassing deep packet inspection and geo-blocks.",
  icon: PlaySquare
}, {
  title: "P2P & Torrenting Safety",
  description: "We evaluate dedicated P2P servers for port forwarding capabilities, bandwidth throttling, and kill-switch reliability during active torrent swarms.",
  icon: Download
}, {
  title: "Privacy Policy Review",
  description: "Legal experts dissect terms of service and privacy policies to identify data logging loopholes, jurisdiction risks, and third-party data sharing clauses.",
  icon: FileText
}, {
  title: "App Usability & Stability",
  description: "Cross-platform stress testing on Windows, macOS, iOS, and Android to evaluate UI responsiveness, connection stability, and resource consumption.",
  icon: Smartphone
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Methodology() {
  return <section id="howwetestmethodology" className="bg-background py-24 px-6 md:px-12 overflow-hidden">
      <div className="mx-auto max-w-6xl">
        <div className="text-center mb-16 md:mb-24">
          <motion.span initial={{
          opacity: 0,
          y: -10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="font-mono text-primary font-semibold tracking-wider uppercase text-sm mb-4 block">
            Our Testing Methodology
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: -10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl md:text-5xl font-bold font-default text-foreground mb-6">
            The Stealth-Tech Protocol
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: -10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
            We don't just take VPN providers at their word. Every service undergoes a rigorous, multi-layered technical audit to ensure your data remains truly invisible.
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {protocols.map((protocol, i) => {
          const Icon = protocol.icon;
          return <motion.div key={i} variants={itemVariants} data-index={i}>
                <Card className="bg-foreground text-background border-none shadow-xl rounded-lg h-full relative overflow-hidden group">
                  {}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary opacity-70 group-hover:opacity-100 transition-opacity" />
                  
                  <CardHeader className="flex flex-row items-start gap-5 pb-3 pt-8 pl-8 pr-6">
                    <div className="bg-primary/10 p-3 rounded-lg shrink-0 border border-primary/20 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="space-y-1 mt-1">
                      <CardTitle className="text-xl font-semibold font-default text-background tracking-tight">
                        {protocol.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pl-8 pr-6 pb-8">
                    <p className="text-background/70 leading-relaxed text-sm md:text-base">
                      {protocol.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}